/**
 * Live feed over SSE (`GET /api/v1/stream`). Uses fetch streaming instead of
 * EventSource so `Last-Event-ID` is
 * sent on reconnect. New items are merged into page 0 of every cached feed
 * query whose filter matches.
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { API_BASE, FeedItem } from '@algoboo/schema/api'
import { kindKey } from './keys'

export type StreamStatus = 'connecting' | 'live' | 'reconnecting' | 'off'

export interface FeedStreamState {
  status: StreamStatus
  lastEventAt: number | null
  freshIds: ReadonlySet<string>
}

export interface SseFrame {
  id?: string
  event: string
  data: string
}

/** Incremental SSE parser: feed chunks, get complete frames back. */
export function createSseParser(): (chunk: string) => SseFrame[] {
  let buffer = ''
  return (chunk) => {
    buffer += chunk
    const frames: SseFrame[] = []
    let idx: number
    while ((idx = buffer.search(/\r?\n\r?\n/)) >= 0) {
      const block = buffer.slice(0, idx)
      const sep = buffer.slice(idx).match(/^\r?\n\r?\n/)![0]
      buffer = buffer.slice(idx + sep.length)
      let event = 'message'
      let id: string | undefined
      const data: string[] = []
      for (const line of block.split(/\r?\n/)) {
        if (!line || line.startsWith(':')) continue
        const colon = line.indexOf(':')
        const field = colon < 0 ? line : line.slice(0, colon)
        let value = colon < 0 ? '' : line.slice(colon + 1)
        if (value.startsWith(' ')) value = value.slice(1)
        if (field === 'event') event = value
        else if (field === 'data') data.push(value)
        else if (field === 'id') id = value
      }
      if (data.length === 0) continue
      const frame: SseFrame = { event, data: data.join('\n') }
      if (id !== undefined) frame.id = id
      frames.push(frame)
    }
    return frames
  }
}

type FeedPages = InfiniteData<{ items: FeedItem[]; nextCursor: string | null }, unknown>

function kindMatches(key: unknown, kind: string): boolean {
  if (typeof key !== 'string' || key === 'all') return true
  return key.split(',').includes(kind)
}

/** Prepend `item` into every cached feed whose filter matches. Returns true if any cache changed. */
export function mergeFeedItem(qc: QueryClient, item: FeedItem): boolean {
  let changed = false
  for (const [key, data] of qc.getQueriesData<FeedPages>({ queryKey: ['feed'] })) {
    if (!data || !data.pages?.length) continue
    const [, scope, a, b] = key as readonly unknown[]
    const matches =
      scope === 'global'
        ? kindMatches(a, item.kind) && (b === null || b === undefined || b === item.agent.handle)
        : scope === 'agent'
          ? a === item.agent.handle && kindMatches(b, item.kind)
          : false
    if (!matches) continue
    if (data.pages.some((p) => p.items.some((x) => x.id === item.id))) continue
    const [first, ...rest] = data.pages
    qc.setQueryData<FeedPages>(key, { ...data, pages: [{ ...first!, items: [item, ...first!.items] }, ...rest] })
    changed = true
  }
  return changed
}

const FRESH_MS = 8_000

interface FeedStreamWiring {
  qc: QueryClient
  kinds: string
  lastIdRef: { current: string | null }
  freshRef: { current: Map<string, number> }
  setStatus: (s: StreamStatus) => void
  setFreshIds: (ids: ReadonlySet<string>) => void
  setLastEventAt: (t: number) => void
}

/**
 * The global feed's SSE connection, reconnecting with backoff from the last event id; returns its close. A module
 * function the hook's effect calls (lane BOO1): the React Compiler does not lower its loop and its `throw` in a try.
 */
function startFeedStream({ qc, kinds, lastIdRef, freshRef, setStatus, setFreshIds, setLastEventAt }: FeedStreamWiring): () => void {
  const controller = new AbortController()
  let attempt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  const seen = new Set<string>()

  const prune = setInterval(() => {
    const now = Date.now()
    let changed = false
    for (const [id, t] of freshRef.current) {
      if (now - t > FRESH_MS) {
        freshRef.current.delete(id)
        changed = true
      }
    }
    if (changed) setFreshIds(new Set(freshRef.current.keys()))
  }, 2_000)

  const onFrame = (frame: SseFrame) => {
    if (frame.id) lastIdRef.current = frame.id
    if (frame.event !== 'post' && frame.event !== 'message') return
    let json: unknown
    try {
      json = JSON.parse(frame.data)
    } catch {
      console.warn('[stream] dropped unparseable event')
      return
    }
    const parsed = FeedItem.safeParse(json)
    if (!parsed.success) {
      console.warn('[stream] dropped invalid FeedItem', parsed.error.issues)
      return
    }
    const item = parsed.data
    if (seen.has(item.id)) return
    seen.add(item.id)
    mergeFeedItem(qc, item)
    freshRef.current.set(item.id, Date.now())
    setFreshIds(new Set(freshRef.current.keys()))
    setLastEventAt(Date.now())
  }

  const connect = async () => {
    setStatus(attempt === 0 ? 'connecting' : 'reconnecting')
    try {
      const headers: Record<string, string> = { Accept: 'text/event-stream' }
      if (lastIdRef.current) headers['Last-Event-ID'] = lastIdRef.current
      const url = `${API_BASE}/stream${kinds === 'all' ? '' : `?kind=${encodeURIComponent(kinds)}`}`
      const res = await globalThis.fetch(url, { headers, credentials: 'include', signal: controller.signal })
      if (!res.ok || !res.body) throw new Error(`stream http ${res.status}`)
      setStatus('live')
      attempt = 0
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      const parse = createSseParser()
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        for (const f of parse(decoder.decode(value, { stream: true }))) onFrame(f)
      }
      throw new Error('stream closed')
    } catch {
      if (controller.signal.aborted) return
      attempt++
      setStatus('reconnecting')
      const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt - 1, 5))
      timer = setTimeout(() => void connect(), delay)
    }
  }
  void connect()

  return () => {
    controller.abort()
    clearInterval(prune)
    if (timer) clearTimeout(timer)
  }
}

export function useFeedStream(opts: { kinds?: string[]; enabled?: boolean } = {}): FeedStreamState {
  const qc = useQueryClient()
  const enabled = opts.enabled ?? true
  const kinds = kindKey(opts.kinds)
  const [status, setStatus] = useState<StreamStatus>(enabled ? 'connecting' : 'off')
  const [lastEventAt, setLastEventAt] = useState<number | null>(null)
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(() => new Set())
  const freshRef = useRef(new Map<string, number>())
  const lastIdRef = useRef<string | null>(null)

  // off: the returned status says so; on: the stream sets 'connecting' as it opens
  useEffect(() => (enabled ? startFeedStream({ qc, kinds, lastIdRef, freshRef, setStatus, setFreshIds, setLastEventAt }) : undefined), [enabled, kinds, qc])

  return { status: enabled ? status : 'off', lastEventAt, freshIds }
}
